import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["aria-label", "actionButton", "appearance", "badge", "cardElevation", "cardGridTemplate", "data", "flexArea", "loading", "onClick", "photoBody", "photoFooter", "photoOrientation", "renderLayout", "renderLoadingLayout", "saveButton"];
import PropTypes from 'prop-types';
import React, { forwardRef, Fragment } from 'react';
import { PROPERTY_CARD_APPEARANCES } from '../../constants';
import { Grid } from '../Grid/Grid';
import { PropertyCardActionArea } from '../PropertyCardActionArea/PropertyCardActionArea';
import { PropertyCardBadge } from '../PropertyCardBadge/PropertyCardBadge';
import { PropertyCardBadgeArea } from '../PropertyCardBadgeArea/PropertyCardBadgeArea';
import { PropertyCardBody } from '../PropertyCardBody/PropertyCardBody';
import { PropertyCardDataArea } from '../PropertyCardDataArea/PropertyCardDataArea';
import { PropertyCardDataWrapper } from '../PropertyCardDataWrapper/PropertyCardDataWrapper';
import { PropertyCardFlexWrapper } from '../PropertyCardFlexWrapper/PropertyCardFlexWrapper';
import { PropertyCardHomeDetails } from '../PropertyCardHomeDetails/PropertyCardHomeDetails';
import { PropertyCardHomeDetailsList } from '../PropertyCardHomeDetailsList/PropertyCardHomeDetailsList';
import { PropertyCardMenuPopper } from '../PropertyCardMenuPopper/PropertyCardMenuPopper';
import { PropertyCardMLSFooter } from '../PropertyCardMLSFooter/PropertyCardMLSFooter';
import { PropertyCardMLSLogo } from '../PropertyCardMLSLogo/PropertyCardMLSLogo';
import { PropertyCardPhoto } from '../PropertyCardPhoto/PropertyCardPhoto';
import { PropertyCardPhotoBody } from '../PropertyCardPhotoBody/PropertyCardPhotoBody';
import { PropertyCardPhotoFallback } from '../PropertyCardPhotoFallback/PropertyCardPhotoFallback';
import { PropertyCardPhotoFooter } from '../PropertyCardPhotoFooter/PropertyCardPhotoFooter';
import { PropertyCardPhotoHeader } from '../PropertyCardPhotoHeader/PropertyCardPhotoHeader';
import { PropertyCardPhotoWrapper } from '../PropertyCardPhotoWrapper/PropertyCardPhotoWrapper';
import { PropertyCardSaveArea } from '../PropertyCardSaveArea/PropertyCardSaveArea';
import { PropertyCardSaveButton } from '../PropertyCardSaveButton/PropertyCardSaveButton';
import { PropertyCardProvider } from './PropertyCard.context';
import { StyledPropertyCard } from './PropertyCard.styles';

/**
 * @extends Card
 * @related ['Carousel', 'PhotoCarousel']
 * @subcomponents ['PropertyCard.Badge', 'PropertyCard.DataArea', 'PropertyCard.HomeDetails',
 * 'PropertyCard.Photo', 'PropertyCard.PhotoFallback', 'PhotoCarousel']
 * @since 8.63.0
 */
export var PropertyCard = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var _data$mls2, _data$mls3, _data$mls4;
  var ariaLabel = props['aria-label'],
    actionButton = props.actionButton,
    appearance = props.appearance,
    badge = props.badge,
    cardElevation = props.cardElevation,
    cardGridTemplate = props.cardGridTemplate,
    data = props.data,
    flexArea = props.flexArea,
    loading = props.loading,
    onClick = props.onClick,
    photoBody = props.photoBody,
    photoFooter = props.photoFooter,
    photoOrientation = props.photoOrientation,
    renderLayout = props.renderLayout,
    renderLoadingLayout = props.renderLoadingLayout,
    saveButton = props.saveButton,
    rest = _objectWithoutProperties(props, _excluded);
  var getDataAreaProps = function getDataAreaProps(dataType) {
    var _data$dataType;
    var dataAreaObj = {};
    // if value is a props obj
    if ((_data$dataType = data[dataType]) !== null && _data$dataType !== void 0 && _data$dataType.children) {
      dataAreaObj = data[dataType];
    }
    // else it's a string, element or other type of node
    else {
      dataAreaObj = {
        children: data[dataType]
      };
    }
    // if Large card, attach body3 to the end of body1
    if (dataType === 'body1' && props.appearance === PROPERTY_CARD_APPEARANCES.LARGE && data.body3) {
      var body1Children = dataAreaObj.children;
      var body3Children = data.body3.children || data.body3;
      dataAreaObj.children = /*#__PURE__*/React.createElement(Fragment, null, body1Children, /*#__PURE__*/React.createElement("span", null, body3Children));
    }
    return dataAreaObj;
  };
  var additionalInfoArea = function additionalInfoArea() {
    var _data$mls;
    var additionalInfoProps = Object.assign({}, getDataAreaProps('additionalInfo'));
    var mlsAttribution = ((_data$mls = data.mls) === null || _data$mls === void 0 ? void 0 : _data$mls.attribution) || '';
    var separator = additionalInfoProps.children && mlsAttribution ? '. ' : '';
    if (typeof additionalInfoProps.children === 'undefined' && mlsAttribution === '') {
      return null;
    }
    return /*#__PURE__*/React.createElement(PropertyCardDataArea, _extends({
      dataType: "additionalInfo",
      lineClamp: "none"
    }, additionalInfoProps), additionalInfoProps.children, separator, mlsAttribution);
  };
  var mlsLogo = (_data$mls2 = data.mls) !== null && _data$mls2 !== void 0 && _data$mls2.logoSrc ? /*#__PURE__*/React.createElement(PropertyCardMLSLogo, {
    logoSrc: (_data$mls3 = data.mls) === null || _data$mls3 === void 0 ? void 0 : _data$mls3.logoSrc,
    attribution: (_data$mls4 = data.mls) === null || _data$mls4 === void 0 ? void 0 : _data$mls4.attribution
  }) : null;
  var defaultLayout = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(PropertyCardBody, {
    "aria-label": ariaLabel,
    cardElevation: cardElevation,
    interactive: onClick ? true : undefined,
    onClick: onClick
  }, /*#__PURE__*/React.createElement(PropertyCardDataWrapper, null, /*#__PURE__*/React.createElement(PropertyCardDataArea, _extends({
    dataType: "title"
  }, getDataAreaProps('title'))), /*#__PURE__*/React.createElement(PropertyCardDataArea, _extends({
    dataType: "body1"
  }, getDataAreaProps('body1'))), /*#__PURE__*/React.createElement(PropertyCardDataArea, _extends({
    dataType: "body2"
  }, getDataAreaProps('body2'))), data.body3 && appearance !== PROPERTY_CARD_APPEARANCES.LARGE && /*#__PURE__*/React.createElement(PropertyCardDataArea, _extends({
    dataType: "body3"
  }, getDataAreaProps('body3'))), (appearance === PROPERTY_CARD_APPEARANCES.LARGE || appearance === PROPERTY_CARD_APPEARANCES.FLAG) && additionalInfoArea(), /*#__PURE__*/React.createElement(PropertyCardActionArea, null, actionButton), appearance === PROPERTY_CARD_APPEARANCES.HORIZONTAL && mlsLogo), /*#__PURE__*/React.createElement(PropertyCardPhotoWrapper, null, /*#__PURE__*/React.createElement(PropertyCardPhotoHeader, null, /*#__PURE__*/React.createElement(PropertyCardBadgeArea, null, badge), /*#__PURE__*/React.createElement(PropertyCardSaveArea, null, saveButton)), /*#__PURE__*/React.createElement(PropertyCardPhotoBody, null, photoBody), /*#__PURE__*/React.createElement(PropertyCardPhotoFooter, null, photoFooter || appearance !== PROPERTY_CARD_APPEARANCES.HORIZONTAL && mlsLogo)), flexArea && /*#__PURE__*/React.createElement(PropertyCardFlexWrapper, null, flexArea)), (appearance === PROPERTY_CARD_APPEARANCES.SMALL || appearance === PROPERTY_CARD_APPEARANCES.HORIZONTAL) && /*#__PURE__*/React.createElement(Grid, {
    display: "inline-grid"
  }, additionalInfoArea()));
  var defaultLoadingLayout = /*#__PURE__*/React.createElement(PropertyCardBody, {
    "aria-label": ariaLabel
  }, /*#__PURE__*/React.createElement(PropertyCardDataWrapper, null, /*#__PURE__*/React.createElement(PropertyCardDataArea, {
    dataType: "title"
  }, "\xA0"), /*#__PURE__*/React.createElement(PropertyCardDataArea, {
    dataType: "body1"
  }, "\xA0"), /*#__PURE__*/React.createElement(PropertyCardDataArea, {
    dataType: "body2"
  }, "\xA0")), /*#__PURE__*/React.createElement(PropertyCardPhotoWrapper, null, /*#__PURE__*/React.createElement(PropertyCardPhotoBody, null)));
  var layoutSection = defaultLayout;
  if (!loading && typeof renderLayout === 'function') {
    layoutSection = renderLayout(props);
  }
  if (loading && typeof renderLoadingLayout === 'function') {
    layoutSection = renderLoadingLayout(props);
  }
  if (loading && typeof renderLoadingLayout !== 'function') {
    layoutSection = defaultLoadingLayout;
  }
  return /*#__PURE__*/React.createElement(PropertyCardProvider, {
    value: {
      appearance: appearance,
      loading: loading,
      onClick: onClick
    }
  }, /*#__PURE__*/React.createElement(StyledPropertyCard, _extends({
    ref: forwardedRef,
    appearance: appearance,
    cardElevation: cardElevation,
    cardGridTemplate: cardGridTemplate,
    interactive: onClick ? true : undefined,
    loading: loading,
    photoOrientation: photoOrientation
    // Override interactive `Card` where role="button".
    // (buttons and links can't live inside of a button)
    ,
    role: undefined
  }, rest), layoutSection));
});
PropertyCard.displayName = 'PropertyCard';
PropertyCard.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Displays a button in the top right of the data area. Usually a
   * `PropertyCard.SaveButton` or `PropertyCard.MenuPopper`.
   *
   * Cannot be used in a Flag card.
   */
  'actionButton': PropTypes.node,
  /**
   * Determines the overall card style.
   */
  'appearance': PropTypes.oneOf(Object.values(PROPERTY_CARD_APPEARANCES)),
  /**
   * This is the text that screen readers announce when this card receives focus.
   * Usually, you'll want to use the address of the property. If no `aria-label` is
   * provided, all data area text is announced.
   */
  'aria-label': PropTypes.string,
  /**
   * Takes a `PropertyCard.Badge` node that will display in the upper-left corner
   * of the photo container.
   *
   * Note that only a Large Card supports displaying a `PropertyCard.Badge` and
   * `PropertyCard.SaveButton` at the same time.
   */
  'badge': PropTypes.node,
  /**
   * This integer determines whether we use a shadow/elevation (1) or no elevation (0).
   */
  'cardElevation': PropTypes.oneOf([0, 1]),
  /**
   * Sets the heights or widths of the photo, data and flex wrappers (in that order).
   *
   * Applied using `grid-template-rows` or `grid-template-columns`, depending on the
   * card appearance.
   *
   * If undefined, default values are provided by the theme.
   *
   * Ex: a Large Card by default is set to "149px 1fr auto" which sets
   * the heights of the photo area to 149px, the data area to 1fr and
   * the Flex area (if it exists) to "auto".
   */
  'cardGridTemplate': PropTypes.string,
  /**
   * The property info text you’d like to display in the card.
   *
   * The `data` object takes up to five data area properties. which will appear in this order
   * by default: title, body1, body2, body3, additionalInfo.
   *
   * Each data area property can take a node or a props object. Nodes (including strings) will be
   * rendered as children of a `PropertyCard.DataArea`. A props object will be spread
   * across a `PropertyCard.DataArea` (a `children` value is required).
   *
   * An additional property called `mls` is used to display the logo and
   * attribution text for an MLS.
   */
  'data': PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    body1: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    body2: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    body3: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    additionalInfo: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    mls: PropTypes.shape({
      attribution: PropTypes.string,
      logoSrc: PropTypes.string
    })
  }),
  /**
   * Custom content that appears after the data areas.
   */
  'flexArea': PropTypes.node,
  /**
   * If true, displays the card in a loading state.
   */
  'loading': PropTypes.bool,
  /**
   * Executed when you click anywhere on the card (unless you've clicked on an
   * interactive child component) or hit ENTER or SPACE while card has focus.
   */
  'onClick': PropTypes.func,
  /**
   * Takes a node that will render across the entire width and height of the photo area.
   * Usually, this will be a `PropertyCard.Photo` or a `PhotoCarousel`.
   */
  'photoBody': PropTypes.node,
  /**
   * Takes a node that will override the default photo area footer, `PropertyCard.MLSLogo`.
   */
  'photoFooter': PropTypes.node,
  /**
   * Render function that can be used to customize the layout of the page. Receives `props`
   * as an argument.
   */
  'renderLayout': PropTypes.func,
  /**
   * Render function that can be used to customize the loading state layout. Receives `props`
   * as an argument.
   */
  'renderLoadingLayout': PropTypes.func,
  /**
   * Displays a button in the top right of the photo area. Usually a
   * `PropertyCard.SaveButton`.
   *
   * Note that only a Large Card supports displaying a `PropertyCard.Badge` and
   * `PropertyCard.SaveButton` at the same time.
   */
  'saveButton': PropTypes.node
} : {};
PropertyCard.defaultProps = {
  'aria-label': '',
  'appearance': PROPERTY_CARD_APPEARANCES.LARGE,
  'cardElevation': 1,
  'loading': false
};
PropertyCard.ActionArea = PropertyCardActionArea;
PropertyCard.Badge = PropertyCardBadge;
PropertyCard.BadgeArea = PropertyCardBadgeArea;
PropertyCard.Body = PropertyCardBody;
PropertyCard.DataArea = PropertyCardDataArea;
PropertyCard.DataWrapper = PropertyCardDataWrapper;
PropertyCard.FlexWrapper = PropertyCardFlexWrapper;
PropertyCard.HomeDetails = PropertyCardHomeDetails;
PropertyCard.HomeDetailsList = PropertyCardHomeDetailsList;
PropertyCard.MenuPopper = PropertyCardMenuPopper;
PropertyCard.MLSFooter = PropertyCardMLSFooter;
PropertyCard.MLSLogo = PropertyCardMLSLogo;
PropertyCard.Photo = PropertyCardPhoto;
PropertyCard.PhotoBody = PropertyCardPhotoBody;
PropertyCard.PhotoFallback = PropertyCardPhotoFallback;
PropertyCard.PhotoFooter = PropertyCardPhotoFooter;
PropertyCard.PhotoHeader = PropertyCardPhotoHeader;
PropertyCard.PhotoWrapper = PropertyCardPhotoWrapper;
PropertyCard.SaveArea = PropertyCardSaveArea;
PropertyCard.SaveButton = PropertyCardSaveButton;